import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getBackendUrl } from "@/lib/api";
import {
  CheckCircle, WarningCircle, ClockCounterClockwise, FloppyDisk, PaperPlaneTilt,
  Trash, Plus, LinkedinLogo, Camera, FilePdf, FileArchive, IdentificationBadge,
  CalendarBlank, UserCircle, EyeSlash,
} from "@phosphor-icons/react";

/* v2.90 · PE Profile editor with draft/submit/approve/pending-revision
   workflow. Material-field edits (country/region/years/specializations
   /certifications) require admin re-approval; cosmetic edits publish
   instantly and mirror straight into `profile_live`. */

const STATUS_META = {
  draft:            { color: "text-[#FFCC00]", border: "border-[#FFCC00]/40", bg: "bg-[#FFCC00]/5", label: "DRAFT", icon: WarningCircle, hint: "Fill out ≥ 60 % of the profile, then submit for admin review." },
  pending:          { color: "text-[#00E5FF]", border: "border-[#00E5FF]/40", bg: "bg-[#00E5FF]/5", label: "PENDING REVIEW", icon: ClockCounterClockwise, hint: "Admin is reviewing your submission — you'll see a decision here." },
  approved:         { color: "text-emerald-400", border: "border-emerald-400/40", bg: "bg-emerald-400/5", label: "LIVE", icon: CheckCircle, hint: "Your profile is live in the public directory. Cosmetic edits publish instantly." },
  pending_revision: { color: "text-[#FFCC00]", border: "border-[#FFCC00]/40", bg: "bg-[#FFCC00]/5", label: "REVISION PENDING", icon: ClockCounterClockwise, hint: "Material changes are queued for admin review — your previous approved version is still live." },
};

const MATERIAL_FIELDS = new Set(["country","region","years_of_practice","specializations","certifications"]);

const F = ({ label, hint, children, material }) => (
  <label className="block">
    <span className="label-tag block mb-1">
      {label}
      {material && <span className="ml-2 text-[9px] text-[#FFCC00]" title="Requires admin re-approval when edited after first approval.">MATERIAL</span>}
    </span>
    {children}
    {hint && <div className="mt-1 text-[10px] text-gray-500 font-mono">{hint}</div>}
  </label>
);

function TagInput({ value, onChange, testid, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div className="border border-white/10 bg-black/40 p-2 flex flex-wrap items-center gap-1.5" data-testid={testid}>
      {value.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 bg-[#00E5FF]/10 text-[#00E5FF] px-2 py-0.5 text-xs font-mono border border-[#00E5FF]/30">
          {t}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} className="ml-1 hover:text-white" data-testid={`${testid}-remove-${t}`}>×</button>
        </span>
      ))}
      <input value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={placeholder}
        className="flex-1 min-w-[120px] bg-transparent text-sm font-mono focus:outline-none px-1 py-0.5"
        data-testid={`${testid}-input`} />
    </div>
  );
}

export default function PEProfileEditor() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ pe_status: "draft", profile: null, profile_live: null, review: null, completion_pct: 0 });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState("");   // "" | "picture" | "portfolio" | "template"
  const picRef = useRef(null);
  const portRef = useRef(null);
  const tmplRef = useRef(null);
  const loadedRef = useRef(false);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/pe/me/profile");
    setState(data);
    setDirty(false);
    loadedRef.current = true;
  }, []);

  useEffect(() => {
    if (user && user.role === "pe" && !loadedRef.current) refresh();
    if (user && user.role !== "pe") nav("/dashboard");
  }, [user, refresh, nav]);

  const patch = (part) => {
    setState((s) => ({ ...s, profile: { ...(s.profile || {}), ...part } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/pe/me/profile", state.profile || {});
      setState(data);
      setDirty(false);
      toast.success(data.pe_status === "pending_revision"
        ? "Saved · material change queued for admin re-approval"
        : data.pe_status === "approved"
          ? "Saved · cosmetic edit published live instantly"
          : "Draft saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (dirty) { await save(); }
    setSubmitting(true);
    try {
      const { data } = await api.post("/pe/me/profile/submit");
      toast.success(data.pe_status === "pending"
        ? "Submitted — admin will review your profile shortly."
        : "Revision submitted — admin will review the changes shortly.");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadFile = async (endpoint, file, tagName) => {
    if (!file) return;
    setUploading(tagName);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success(`${tagName === "picture" ? "Picture" : tagName === "portfolio" ? "Portfolio file" : "Template"} uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const removeEntry = async (endpoint) => {
    try {
      await api.delete(endpoint);
      await refresh();
      toast.info("Removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
    }
  };

  const meta = STATUS_META[state.pe_status] || STATUS_META.draft;
  const StatusIcon = meta.icon;
  const p = state.profile || {};
  const canSubmit = (state.pe_status === "draft" || state.pe_status === "pending_revision") && state.completion_pct >= 60;
  const cannotSubmitReason = state.completion_pct < 60 ? `Profile ${state.completion_pct}% complete — need ≥ 60 %` : "";
  const assetUrl = (ref) => ref ? `${getBackendUrl()}/api/pe/assets/${ref}` : "";

  const ring = useMemo(() => {
    const pct = state.completion_pct;
    const r = 22, c = 2 * Math.PI * r;
    return { r, c, offset: c * (1 - pct / 100) };
  }, [state.completion_pct]);

  if (!state.profile) {
    return (
      <div className="industrial min-h-screen bg-[#0A0C10]">
        <Topbar />
        <div className="p-10 text-gray-500 font-mono text-sm">Loading profile…</div>
      </div>
    );
  }

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-tag">// pe profile</div>
            <h1 className="font-display text-4xl md:text-5xl font-black mt-1">Your profile</h1>
            <p className="text-gray-400 mt-2 max-w-2xl">
              Material fields (country, region, years, specializations, certifications) need admin re-approval when edited. Everything else publishes instantly.
            </p>
          </div>
          <div className="relative w-14 h-14" title={`${state.completion_pct}% complete`} data-testid="pe-completion-ring">
            <svg viewBox="0 0 60 60" className="w-14 h-14 -rotate-90">
              <circle cx="30" cy="30" r={ring.r} stroke="rgba(255,255,255,0.08)" strokeWidth="4" fill="none" />
              <circle cx="30" cy="30" r={ring.r} stroke="#00E5FF" strokeWidth="4" fill="none"
                strokeDasharray={ring.c} strokeDashoffset={ring.offset} strokeLinecap="round"
                className="transition-all duration-500" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-xs font-mono text-[#00E5FF]">{state.completion_pct}%</div>
          </div>
        </div>

        <div className={`mt-6 border ${meta.border} ${meta.bg} p-4 flex items-start gap-3`} data-testid="pe-status-card">
          <StatusIcon size={22} weight="duotone" className={`${meta.color} flex-none mt-0.5`} />
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-mono ${meta.color} font-bold`}>{meta.label}</div>
            <div className="text-xs text-gray-400 mt-0.5">{meta.hint}</div>
            {state.review?.feedback && (
              <div className="mt-3 border-l-2 border-white/20 pl-3 py-1 text-xs font-mono text-gray-300"
                   data-testid="pe-admin-feedback">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Admin feedback</div>
                {state.review.feedback}
              </div>
            )}
          </div>
        </div>

        {/* ─── Location & experience ─────────────────────────────────── */}
        <section className="mt-8 border border-white/10 bg-[#12151C] p-6">
          <div className="label-tag mb-4">// location &amp; experience</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Country" material>
              <input value={p.country || ""} onChange={(e) => patch({ country: e.target.value })}
                className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                placeholder="Canada" data-testid="pe-country" />
            </F>
            <F label="State / province" material>
              <input value={p.region || ""} onChange={(e) => patch({ region: e.target.value })}
                className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                placeholder="Ontario" data-testid="pe-region" />
            </F>
            <F label="Years of practice" material>
              <input type="number" min={0} max={80} value={p.years_of_practice || 0}
                onChange={(e) => patch({ years_of_practice: Number(e.target.value) })}
                className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                data-testid="pe-years" />
            </F>
          </div>
        </section>

        {/* ─── Specializations & competencies ────────────────────────── */}
        <section className="mt-6 border border-white/10 bg-[#12151C] p-6">
          <div className="label-tag mb-4">// specializations &amp; competencies</div>
          <F label="Specializations (searchable in the directory)" material
             hint="Press Enter or comma to add. Clients filter by these.">
            <TagInput value={p.specializations || []}
              onChange={(v) => patch({ specializations: v })}
              testid="pe-specs" placeholder="e.g. Arc-flash studies" />
          </F>
          <div className="h-4" />
          <F label="Competencies (tools, standards, software)"
             hint="Cosmetic — publishes instantly when you save.">
            <TagInput value={p.competencies || []}
              onChange={(v) => patch({ competencies: v })}
              testid="pe-competencies" placeholder="e.g. NEC design, ETAP, IEEE 1584-2018" />
          </F>
        </section>

        {/* ─── Certifications ────────────────────────────────────────── */}
        <section className="mt-6 border border-white/10 bg-[#12151C] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-tag">// certifications &amp; licenses</div>
            <button onClick={() => patch({ certifications: [...(p.certifications || []), { name: "", number: "", issued: "" }] })}
              className="text-xs text-[#00E5FF] hover:underline font-mono" data-testid="pe-cert-add">
              <Plus size={12} className="inline" /> add
            </button>
          </div>
          <div className="space-y-2">
            {(p.certifications || []).map((c, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start" data-testid={`pe-cert-row-${i}`}>
                <input value={c.name || ""} onChange={(e) => {
                  const list = [...(p.certifications || [])]; list[i] = { ...list[i], name: e.target.value }; patch({ certifications: list });
                }} placeholder="P.Eng. Ontario"
                  className="md:col-span-5 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" data-testid={`pe-cert-name-${i}`} />
                <input value={c.number || ""} onChange={(e) => {
                  const list = [...(p.certifications || [])]; list[i] = { ...list[i], number: e.target.value }; patch({ certifications: list });
                }} placeholder="License #"
                  className="md:col-span-3 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" data-testid={`pe-cert-num-${i}`} />
                <input value={c.issued || ""} onChange={(e) => {
                  const list = [...(p.certifications || [])]; list[i] = { ...list[i], issued: e.target.value }; patch({ certifications: list });
                }} placeholder="2015-05"
                  className="md:col-span-3 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" data-testid={`pe-cert-issued-${i}`} />
                <button type="button" onClick={() => patch({ certifications: (p.certifications || []).filter((_, idx) => idx !== i) })}
                  className="md:col-span-1 text-[#FF3B30] hover:underline text-xs font-mono" data-testid={`pe-cert-remove-${i}`}>
                  <Trash size={14} className="inline" />
                </button>
              </div>
            ))}
            {(p.certifications || []).length === 0 && (
              <div className="text-xs text-gray-500 font-mono">No certifications yet — click <b>add</b> to enter your first license.</div>
            )}
            <div className="text-[10px] text-[#FFCC00] font-mono mt-2">MATERIAL — edits queue for admin re-approval.</div>
          </div>
        </section>

        {/* ─── Bio, hobbies, LinkedIn ───────────────────────────────── */}
        <section className="mt-6 border border-white/10 bg-[#12151C] p-6">
          <div className="label-tag mb-4">// about you</div>
          <F label="Short bio (max 500 chars)"
             hint="Cosmetic — publishes instantly.">
            <textarea value={p.bio || ""}
              onChange={(e) => patch({ bio: e.target.value.slice(0, 500) })}
              rows={4}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none resize-none"
              placeholder="Toronto-based P.Eng. specialising in…"
              data-testid="pe-bio" />
          </F>
          <div className="h-4" />
          <F label="Hobbies / outside interests"
             hint="Cosmetic — a human touch for the directory profile.">
            <input value={p.hobbies || ""} onChange={(e) => patch({ hobbies: e.target.value })}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
              placeholder="Rock climbing, vintage HVAC" data-testid="pe-hobbies" />
          </F>
          <div className="h-4" />
          <F label="LinkedIn URL" hint="Cosmetic.">
            <div className="flex items-center gap-2 border border-white/10 bg-black/40 px-3 focus-within:border-[#00E5FF]">
              <LinkedinLogo size={16} className="text-gray-500 flex-none" />
              <input value={p.linkedin_url || ""} onChange={(e) => patch({ linkedin_url: e.target.value })}
                className="flex-1 bg-transparent py-2 text-sm font-mono outline-none"
                placeholder="https://www.linkedin.com/in/your-handle/" data-testid="pe-linkedin" />
            </div>
          </F>
          <div className="h-4" />
          {/* v3.2.21 · Booking URL — a Teams / Calendly / Cal.com URL that
              lets clients book a meeting straight from the public profile. */}
          <F label="Teams / Calendly booking URL"
             hint="Optional. Clients see a `Book a meeting` button on your public profile that opens this URL in a new tab. Paste your Teams meeting link, Calendly, Cal.com, or any scheduler.">
            <div className="flex items-center gap-2 border border-white/10 bg-black/40 px-3 focus-within:border-[#00E5FF]">
              <CalendarBlank size={16} className="text-gray-500 flex-none" />
              <input value={p.booking_url || ""} onChange={(e) => patch({ booking_url: e.target.value })}
                className="flex-1 bg-transparent py-2 text-sm font-mono outline-none"
                placeholder="https://calendly.com/your-handle/30min"
                data-testid="pe-booking-url" />
            </div>
          </F>
        </section>

        {/* ─── Picture ─────────────────────────────────────────────── */}
        <section className="mt-6 border border-white/10 bg-[#12151C] p-6">
          <div className="label-tag mb-4">// profile picture</div>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 border border-white/10 bg-black/40 overflow-hidden flex items-center justify-center"
                 data-testid="pe-pic-preview">
              {p.picture_ref
                ? <img src={assetUrl(p.picture_ref)} alt="profile" className="w-full h-full object-cover" />
                : <IdentificationBadge size={40} weight="duotone" className="text-gray-600" />}
            </div>
            <div>
              <input type="file" ref={picRef} accept="image/png,image/jpeg,image/webp" hidden
                onChange={(e) => uploadFile("/pe/me/profile/picture", e.target.files?.[0], "picture")}
                data-testid="pe-pic-input" />
              <button onClick={() => picRef.current?.click()} disabled={uploading === "picture"}
                className="inline-flex items-center gap-2 border border-[#00E5FF]/40 text-[#00E5FF] px-4 py-2 text-sm font-mono hover:bg-[#00E5FF]/10 disabled:opacity-40"
                data-testid="pe-pic-upload">
                <Camera size={16} /> {uploading === "picture" ? "Uploading…" : p.picture_ref ? "Replace" : "Upload"}
              </button>
              <div className="text-[10px] text-gray-500 font-mono mt-1">PNG/JPEG/WebP · max 3 MB · cosmetic.</div>
            </div>
          </div>
        </section>

        {/* ─── Portfolio ────────────────────────────────────────────── */}
        <section className="mt-6 border border-white/10 bg-[#12151C] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-tag">// portfolio · images + PDFs</div>
            <input type="file" ref={portRef} accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" hidden
              onChange={(e) => uploadFile("/pe/me/profile/portfolio", e.target.files?.[0], "portfolio")}
              data-testid="pe-port-input" />
            <button onClick={() => portRef.current?.click()} disabled={uploading === "portfolio"}
              className="inline-flex items-center gap-1 text-xs text-[#00E5FF] hover:underline font-mono"
              data-testid="pe-port-upload">
              <Plus size={12} /> {uploading === "portfolio" ? "uploading…" : "add file"}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(p.portfolio || []).map((item) => (
              <div key={item.ref} className="border border-white/10 bg-black/40 p-2 group relative"
                   data-testid={`pe-port-${item.ref}`}>
                {item.content_type?.startsWith("image/")
                  ? <img src={assetUrl(item.ref)} alt="" className="w-full h-24 object-cover" />
                  : <div className="h-24 flex items-center justify-center text-gray-500"><FilePdf size={40} weight="duotone" /></div>}
                <div className="mt-2 text-[11px] font-mono truncate" title={item.title}>{item.title}</div>
                <button onClick={() => removeEntry(`/pe/me/profile/portfolio/${item.ref}`)}
                  className="absolute top-1 right-1 text-[#FF3B30] opacity-0 group-hover:opacity-100 bg-black/60 p-1 text-xs"
                  data-testid={`pe-port-remove-${item.ref}`}><Trash size={12} /></button>
              </div>
            ))}
            {(p.portfolio || []).length === 0 && (
              <div className="col-span-full text-xs text-gray-500 font-mono">No portfolio items yet.</div>
            )}
          </div>
        </section>

        {/* ─── Templates ──────────────────────────────────────────── */}
        <section className="mt-6 border border-white/10 bg-[#12151C] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label-tag">// template packs · DXF / DWG</div>
            <input type="file" ref={tmplRef} hidden
              onChange={(e) => uploadFile("/pe/me/profile/template", e.target.files?.[0], "template")}
              data-testid="pe-tmpl-input" />
            <button onClick={() => tmplRef.current?.click()} disabled={uploading === "template"}
              className="inline-flex items-center gap-1 text-xs text-[#00E5FF] hover:underline font-mono"
              data-testid="pe-tmpl-upload">
              <Plus size={12} /> {uploading === "template" ? "uploading…" : "add template"}
            </button>
          </div>
          <div className="space-y-1">
            {(p.templates || []).map((item) => (
              <div key={item.ref} className="flex items-center justify-between border border-white/10 bg-black/40 px-3 py-2"
                   data-testid={`pe-tmpl-${item.ref}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileArchive size={16} className="text-[#00E5FF] flex-none" />
                  <span className="text-xs font-mono truncate">{item.title}</span>
                  <span className="text-[10px] text-gray-500 font-mono">· {(item.size_bytes/1024).toFixed(0)} KB</span>
                </div>
                <button onClick={() => removeEntry(`/pe/me/profile/template/${item.ref}`)}
                  className="text-[#FF3B30] hover:underline text-xs" data-testid={`pe-tmpl-remove-${item.ref}`}>
                  <Trash size={12} className="inline" /> remove
                </button>
              </div>
            ))}
            {(p.templates || []).length === 0 && (
              <div className="text-xs text-gray-500 font-mono">No template packs yet.</div>
            )}
          </div>
        </section>

        {/* ─── v3.2.55 · Incognito toggle ────────────────────────── */}
        <IncognitoToggle />

        {/* ─── Action bar ──────────────────────────────────────────── */}
        <div className="mt-8 sticky bottom-0 border-t border-white/10 bg-[#0A0C10]/95 backdrop-blur py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs font-mono text-gray-400">
            {dirty
              ? <span className="text-[#FFCC00]">● Unsaved changes</span>
              : <span className="text-gray-500">All changes saved</span>}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={!dirty || saving}
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-sm font-mono hover:bg-white/5 disabled:opacity-40"
              data-testid="pe-save">
              <FloppyDisk size={16} /> {saving ? "Saving…" : "Save draft"}
            </button>
            <button onClick={submit}
              disabled={!canSubmit || submitting || (dirty && !MATERIAL_FIELDS.size)}
              title={!canSubmit ? cannotSubmitReason : ""}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="pe-submit">
              <PaperPlaneTilt size={16} weight="fill" />
              {submitting ? "Submitting…"
                : state.pe_status === "pending_revision" ? "Submit revision"
                : state.pe_status === "draft" ? "Submit for review"
                : state.pe_status === "pending" ? "Awaiting admin…"
                : "Live"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * v3.2.55 · IncognitoToggle — PE-only section that lets the engineer
 * hide their name + profile picture from the public directory,
 * detail page, and proposal cards. Every other profile field remains
 * visible so clients can still evaluate on skills + experience.
 * Real name is revealed to the client the moment the PE is awarded
 * a job, and admins always see the real name.
 */
function IncognitoToggle() {
  const [on, setOn]   = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setOn(!!data.incognito);
      } catch { /* ignore */ }
      finally { setLoaded(true); }
    })();
  }, []);

  const loadPreview = useCallback(async () => {
    try {
      const { data } = await api.get("/pe/incognito/preview");
      setPreview(data);
    } catch { setPreview(null); }
  }, []);

  useEffect(() => {
    if (on) loadPreview();
    else setPreview(null);
  }, [on, loadPreview]);

  const flip = async () => {
    const next = !on;
    setBusy(true);
    try {
      await api.patch("/pe/incognito", { incognito: next });
      setOn(next);
      toast.success(next
        ? "You're now incognito. Your name + picture are hidden from public views."
        : "Incognito off. Your name + picture are visible again.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  if (!loaded) return null;

  return (
    <section className="mt-6 border border-white/10 bg-black/40 p-6" data-testid="pe-incognito">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="label-tag">// privacy</div>
          <h3 className="mt-2 font-display text-xl font-bold text-white">Go incognito</h3>
          <p className="mt-2 text-sm text-gray-400 max-w-xl leading-relaxed">
            Hides your <strong>name</strong> and <strong>profile picture</strong> from the public
            directory, the PE detail page, and the AI proposal leaderboard. Everything else
            stays visible so clients can still evaluate you on skills, experience, and portfolio.
            Once you&apos;re awarded a job, the hiring client automatically sees your real name.
            Admins always see the real name.
          </p>
        </div>
        <button
          type="button"
          onClick={flip}
          disabled={busy}
          role="switch"
          aria-checked={on}
          className={`relative w-16 h-8 border-2 transition-colors flex-shrink-0 disabled:opacity-50
                       ${on ? "bg-[#00E5FF] border-[#00E5FF]" : "bg-transparent border-white/30"}`}
          data-testid="pe-incognito-toggle"
        >
          <span
            className={`absolute top-0.5 w-6 h-6 bg-white transition-all
                         ${on ? "left-8" : "left-0.5"}`}
          />
        </button>
      </div>
      {on && (
        <div className="mt-4 border-l-2 border-[#00E5FF] pl-3 text-xs font-mono text-gray-400">
          <span className="text-[#00E5FF]">Public handle:</span>{" "}
          <span className="text-white">{preview?.name || "Anonymous PE #XXXXXX"}</span>
          {"  ·  "}<span className="text-gray-500">last 6 chars of your ID.</span>
        </div>
      )}
      {on && preview && <IncognitoPreviewCard preview={preview} />}
    </section>
  );
}


/**
 * v3.2.55 · IncognitoPreviewCard — Live "this is how clients see you
 * right now" preview. Mirrors the exact anonymised payload returned
 * by `/api/pe/incognito/preview` so the PE can confirm at a glance
 * what identity information is (and isn't) leaking. Auto-refreshes
 * whenever the incognito toggle flips on.
 */
function IncognitoPreviewCard({ preview }) {
  const specs = (preview.specializations || []).slice(0, 6);
  const comps = (preview.competencies || []).slice(0, 6);
  return (
    <div className="mt-5 border border-[#00E5FF]/30 bg-[#00E5FF]/[0.03]"
          data-testid="pe-incognito-preview">
      <div className="flex items-center justify-between gap-2 border-b border-[#00E5FF]/20 bg-[#00E5FF]/[0.06] px-4 py-2">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#00E5FF]">
          <EyeSlash size={14} weight="bold" />
          Client preview · live
        </div>
        <div className="text-[10px] font-mono text-gray-500">
          exactly what clients see on the directory
        </div>
      </div>
      <div className="p-4 flex gap-4 items-start">
        {/* Masked avatar — never renders picture_ref even if server bug leaks it */}
        <div className="w-16 h-16 border border-white/10 bg-black/60 flex items-center justify-center flex-shrink-0"
              data-testid="pe-incognito-preview-avatar">
          <UserCircle size={40} weight="thin" className="text-gray-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-bold text-white truncate"
                data-testid="pe-incognito-preview-name">
            {preview.name || "Anonymous PE"}
          </div>
          <div className="text-[11px] font-mono text-gray-500 mt-0.5">
            {[preview.region, preview.country].filter(Boolean).join(", ") || "— region hidden by profile"}
            {preview.years_of_practice ? `  ·  ${preview.years_of_practice} yr practice` : ""}
          </div>
          {preview.bio && (
            <p className="mt-2 text-sm text-gray-300 line-clamp-3">{preview.bio}</p>
          )}
          {(specs.length > 0 || comps.length > 0) && (
            <div className="mt-2 flex flex-wrap gap-1">
              {specs.map((s) => (
                <span key={`s-${s}`} className="text-[10px] font-mono border border-white/15 px-1.5 py-0.5 text-gray-300">
                  {s}
                </span>
              ))}
              {comps.map((c) => (
                <span key={`c-${c}`} className="text-[10px] font-mono border border-[#00E5FF]/30 px-1.5 py-0.5 text-[#00E5FF]">
                  {c}
                </span>
              ))}
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] font-mono">
            <div className="flex items-center gap-1 text-emerald-400">
              <CheckCircle size={12} weight="fill" /> Skills, bio, region visible
            </div>
            <div className="flex items-center gap-1 text-rose-400">
              <WarningCircle size={12} weight="fill" /> Real name hidden
            </div>
            <div className="flex items-center gap-1 text-rose-400">
              <WarningCircle size={12} weight="fill" /> Profile picture hidden
            </div>
            <div className="flex items-center gap-1 text-rose-400">
              <WarningCircle size={12} weight="fill" /> LinkedIn URL hidden
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
